<template>
    <b-container id="locations" v-if="$can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Location Management</h3>
                    </b-col>
                    <b-col cols="4" class="text-right">
                        <b-button variant="primary" @click="openCreateModal" class="btn-sm">
                            <i class="fas fa-plus"></i> New Location
                        </b-button>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-table
                    :items="locations"
                    :fields="fields"
                    :busy="isBusy"
                    hover
                    responsive
                    striped
                    :per-page="perPage"
                    :current-page="currentPage"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    @filtered="onFiltered"
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(name)="row">
                        <strong>{{ row.item.name }}</strong>
                        <b-badge v-if="row.item.is_default" variant="primary" class="ml-1">Default</b-badge>
                    </template>

                    <template v-slot:cell(warehouse_code)="row">
                        <span v-if="row.item.warehouse_code">{{ row.item.warehouse_code }}</span>
                        <span v-else class="text-muted">—</span>
                    </template>

                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(usage_count)="row">
                        <b-badge :variant="row.item.usage_count > 0 ? 'info' : 'light'">
                            {{ row.item.usage_count }} reference(s)
                        </b-badge>
                    </template>

                    <template v-slot:cell(stock_rows)="row">
                        <b-badge variant="light">
                            {{ row.item.stock_rows }} item row(s)
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="editLocation(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button
                                variant="outline-danger"
                                @click="deleteLocation(row.item)"
                                :disabled="row.item.is_default || row.item.usage_count > 0"
                            >
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>

                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="locations-table"
                    class="mt-3"
                ></b-pagination>
            </b-col>
        </b-row>

        <b-modal
            :id="modalId"
            :title="modalTitle"
            @hidden="resetModal"
            @ok="saveLocation"
            :ok-disabled="!isFormValid"
        >
            <b-form @submit.prevent="saveLocation">
                <b-form-group label="Name" label-for="name" :invalid-feedback="errors.name">
                    <b-form-input
                        id="name"
                        v-model="form.name"
                        :state="errors.name ? false : null"
                        required
                    ></b-form-input>
                </b-form-group>

                <b-form-group
                    label="Warehouse Code"
                    label-for="warehouse_code"
                    description="Legacy code used during inventory migration (e.g. a, b, c)."
                    :invalid-feedback="errors.warehouse_code"
                >
                    <b-form-input
                        id="warehouse_code"
                        v-model="form.warehouse_code"
                        :state="errors.warehouse_code ? false : null"
                    ></b-form-input>
                </b-form-group>

                <b-form-group label="Sort Order" label-for="sort_order" :invalid-feedback="errors.sort_order">
                    <b-form-input
                        id="sort_order"
                        v-model.number="form.sort_order"
                        type="number"
                        min="0"
                        :state="errors.sort_order ? false : null"
                    ></b-form-input>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.active" switch>
                        Active
                    </b-form-checkbox>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="form.is_default" switch>
                        Default Location
                    </b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="closeModal">Cancel</b-button>
                <b-button variant="primary" @click="saveLocation" :disabled="!isFormValid">
                    {{ isEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'Locations',

    data() {
        return {
            locations: [],
            isBusy: false,
            perPage: 10,
            currentPage: 1,
            totalRows: 0,
            sortBy: 'sort_order',
            sortDesc: false,
            fields: [
                { key: 'name', label: 'Name', sortable: true },
                { key: 'warehouse_code', label: 'Code', sortable: true },
                { key: 'sort_order', label: 'Sort Order', sortable: true },
                { key: 'active', label: 'Status', sortable: true },
                { key: 'usage_count', label: 'Usage', sortable: true },
                { key: 'stock_rows', label: 'Stock Rows', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            modalId: 'location-modal',
            isEdit: false,
            form: {
                id: null,
                name: '',
                warehouse_code: '',
                sort_order: 0,
                active: true,
                is_default: false
            },
            errors: {}
        }
    },

    computed: {
        modalTitle() {
            return this.isEdit ? 'Edit Location' : 'Create Location'
        },

        isFormValid() {
            return this.form.name.trim() !== ''
        }
    },

    methods: {
        async loadLocations() {
            this.isBusy = true
            try {
                const response = await axios.get('/api/company/locations')
                if (response.data.status === 'success') {
                    this.locations = response.data.locations
                    this.totalRows = this.locations.length
                }
            } catch (error) {
                this.showAlert('error', 'Error loading locations', error.response?.data?.message || 'Unknown error')
            } finally {
                this.isBusy = false
            }
        },

        openCreateModal() {
            this.isEdit = false
            this.resetForm()
            this.$bvModal.show(this.modalId)
        },

        editLocation(location) {
            this.isEdit = true
            this.form = {
                ...location,
                warehouse_code: location.warehouse_code || '',
                active: Boolean(location.active),
                is_default: Boolean(location.is_default)
            }
            this.$bvModal.show(this.modalId)
        },

        async saveLocation() {
            this.errors = {}

            try {
                const url = this.isEdit ? `/api/company/locations/${this.form.id}` : '/api/company/locations'
                const method = this.isEdit ? 'put' : 'post'
                const payload = {
                    ...this.form,
                    warehouse_code: this.form.warehouse_code ? this.form.warehouse_code.trim() : null
                }
                const response = await axios[method](url, payload)

                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.closeModal()
                    this.loadLocations()
                }
            } catch (error) {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors
                } else {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },

        async deleteLocation(location) {
            if (location.is_default) {
                this.showAlert('error', 'Cannot Delete', 'The default location cannot be deleted')
                return
            }

            if (location.usage_count > 0) {
                this.showAlert('error', 'Cannot Delete', 'This location is referenced by existing records')
                return
            }

            if (confirm(`Are you sure you want to delete "${location.name}"?`)) {
                try {
                    const response = await axios.delete(`/api/company/locations/${location.id}`)
                    if (response.data.status === 'success') {
                        this.showAlert('success', 'Success', response.data.message)
                        this.loadLocations()
                    }
                } catch (error) {
                    this.showAlert('error', 'Error', error.response?.data?.message || 'Unknown error')
                }
            }
        },

        resetForm() {
            this.form = {
                id: null,
                name: '',
                warehouse_code: '',
                sort_order: 0,
                active: true,
                is_default: false
            }
            this.errors = {}
        },

        closeModal() {
            this.$bvModal.hide(this.modalId)
            this.resetForm()
        },

        resetModal() {
            this.resetForm()
        },

        onFiltered(filteredItems) {
            this.totalRows = filteredItems.length
            this.currentPage = 1
        },

        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        }
    },

    mounted() {
        this.loadLocations()
    }
}
</script>

<style scoped>
#locations {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}

.table th {
    border-top: none;
}

.badge {
    font-size: 0.75em;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}
</style>
